import CampaignIcon from "@mui/icons-material/Campaign";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getSystemConfig } from "../../../application/selectors/auth";
import ExhibitionBoothLeadApi from "../../../infrastructure/api/exhibitionBoothLead";
import PanelInfo from "../../components/shared/panel-info";

const mobileRegex = /^09\d{9}$/;

const ROLE_OPTIONS = [
  { value: 0, label: "متقاضی" },
  { value: 1, label: "پیمانکار" },
  { value: 2, label: "تامین‌کننده" },
];

const normalizeMobile = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  if (d.startsWith("989") && d.length === 12) return `0${d.slice(2)}`;
  if (d.startsWith("9") && d.length === 10 && !d.startsWith("09")) return `0${d}`;
  return d;
};

const PublicExhibitionBoothPage = () => {
  const theme = useTheme();
  const { data: configs } = useSelector(getSystemConfig);
  const brandTitle = configs?.panelTitle || "سولارنت";

  const [mobile, setMobile] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const norm = normalizeMobile(mobile);
    if (!mobileRegex.test(norm)) {
      toast.error("شماره موبایل معتبر وارد کنید (۱۱ رقم با ۰۹)");
      return;
    }

    try {
      setLoading(true);
      const res = await ExhibitionBoothLeadApi.submitPublic({
        mobile: norm,
        fullName: fullName || undefined,
        role: Number(role),
        source: "exhibition-booth",
      });
      if (res?.isSuccess) {
        setDone(true);
        toast.success(res.message || "ثبت شد");
      } else {
        toast.error(res?.message || "خطا در ثبت");
      }
    } catch {
      toast.error("خطا در ارتباط با سرور");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setMobile("");
    setFullName("");
    setRole(0);
    setDone(false);
  };

  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: "100vh",
        py: { xs: 2, sm: 4 },
        px: 2,
        background: `linear-gradient(165deg, ${alpha(
          theme.palette.primary.main,
          0.14
        )} 0%, ${alpha(theme.palette.warning.main, 0.1)} 45%, ${alpha(
          theme.palette.primary.light,
          0.08
        )} 100%)`,
      }}
    >
      <Container maxWidth="sm" sx={{ px: { xs: 1, sm: 2 } }}>
        <Card
          elevation={0}
          sx={{
            mx: "auto",
            overflow: "hidden",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 12px 40px rgba(25, 118, 210, 0.12)",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 4,
              textAlign: "center",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: "primary.contrastText",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 88,
                height: 88,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.95)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                mb: 2,
                p: 1.25,
              }}
            >
              <PanelInfo size={56} />
            </Box>
            <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
              غرفه سولارنت
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.95, fontWeight: 500 }}>
              {brandTitle} — نمایشگاه خورشیدی
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.88, mt: 1.5 }}>
              شماره موبایل خود را وارد کنید؛ لینک سولارنت برای شما پیامک می‌شود.
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            {done ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                اطلاعات شما ثبت شد. پیامک حاوی لینک سولارنت به زودی برای شما ارسال می‌شود.
                <Box sx={{ mt: 2 }}>
                  <Button variant="contained" size="small" onClick={resetForm}>
                    ثبت شماره جدید
                  </Button>
                </Box>
              </Alert>
            ) : (
              <Box component="form" onSubmit={onSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CampaignIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2" color="text.secondary">
                        فرم ثبت بازدیدکننده غرفه
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="شماره موبایل"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      placeholder="09123456789"
                      inputProps={{ inputMode: "numeric" }}
                      size="medium"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="نام و نام خانوادگی (اختیاری)"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      size="medium"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth required size="medium">
                      <InputLabel id="exhibition-role-label">نوع مخاطب</InputLabel>
                      <Select
                        labelId="exhibition-role-label"
                        label="نوع مخاطب"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                      >
                        {ROLE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={loading}
                      sx={{ py: 1.25, fontWeight: 700 }}
                    >
                      {loading ? "در حال ارسال…" : "ثبت و ارسال پیامک"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default PublicExhibitionBoothPage;
